import { useEffect, useMemo } from 'react';
import { getSortField, getSortValue, sortKey } from '../../../../features/table/helpers';
import { formatDateTimeRangeFromString } from '../../../../helpers/date-time';
import {
  defaultPage,
  defaultPageSize,
  defaultViewStyle,
  overlayKey,
  pageKey,
  pageSizeKey,
  viewStyleKey,
} from '../../../../helpers/path';
import { useFlashElement } from '../../../../hooks/use-flash-element/use-flash-element';
import { useLang } from '../../../../hooks/use-lang';
import { useQueryParams } from '../../../../hooks/use-query-params';
import { TaskStatusType, TaskTypeType } from '../../../../store/api/tasks-api';
import { useBranchesStore } from '../../../../store/use-branch-store';
import { useEmployeesStore } from '../../../../store/use-employee-store';
import { useTaskStore } from '../../../../store/use-task-store';
import { SortType, VariantType } from '../../../../types/props';
import { useLocation } from 'react-router-dom';
import { useModalParams } from '../../../../hooks/use-modal-params';

export const statusVariantMap: Record<TaskStatusType, VariantType> = {
  PROCESS: 'info',
  RESERVED: 'warning',
  PROGRESS: 'warning',
  READY_FOR_PICKUP: 'warning',
  SENT_TO_KITCHEN: 'warning',
  RESERVED_BY_ANOTHER_STAFF: 'warning',
  DONE: 'success',
  DONE_BY_ANOTHER_STAFF: 'success',
  DELIVERED: 'success',
  FAKE: 'danger',
  REJECTED_BY_ANOTHER_STAFF: 'danger',
  REJECTED_BY_EXECUTOR: 'danger',
  CANCELLED_BY_KITCHEN: 'danger',
  EMPLOYEE_REQUIRES: 'danger',
  AUTO_ARCHIVE: 'secondary',
};

export const statusTitleMap: Record<TaskStatusType, string> = {
  PROCESS: 'Новая', // костыль на бэке
  RESERVED: 'Выполняется',
  PROGRESS: 'В процессе',
  READY_FOR_PICKUP: 'Готово к выдаче',
  SENT_TO_KITCHEN: 'Отправлено на кухню',
  DONE: 'Выполнено',
  DONE_BY_ANOTHER_STAFF: 'Выполнено другим сотрудником',
  REJECTED_BY_ANOTHER_STAFF: 'Отклонено другим сотрудником',
  REJECTED_BY_EXECUTOR: 'Отклонено исполнителем',
  CANCELLED_BY_KITCHEN: 'Отменено кухней',
  DELIVERED: 'Доставлено',
  FAKE: 'Ложное',
  AUTO_ARCHIVE: 'Автоархив',
  RESERVED_BY_ANOTHER_STAFF: 'Выполняется другим сотрудником',
  EMPLOYEE_REQUIRES: 'Требует назначения ответственного',
};

export const taskTypeMap: Record<TaskTypeType, string> = {
  ORDER: 'Заказ',
  ACTION: 'Услуга',
  COMMON: 'Админ',
};

export const changeStatuses: TaskStatusType[] = ['RESERVED', 'PROGRESS', 'PROCESS'];

export const useTaskList = () => {
  const { t } = useLang();

  const { fetchTaskList, list, isLoadingFetch, pagination } = useTaskStore();
  const { fetchBranches } = useBranchesStore();
  const { fetchEmployees } = useEmployeesStore();

  const { getQueryParams } = useQueryParams();
  const location = useLocation();

  const type = location.pathname.split('/')[3].toUpperCase() as TaskTypeType;

  const params = getQueryParams();
  const viewStyle = params[viewStyleKey] || defaultViewStyle;

  const qrCode = params.qrCode;
  const name = params.name;
  const employee = params.employee;
  const status = params.status as string;
  const comment = params.comment;
  const clientComment = params.client_comment;
  const branchId = params['branch'];
  const createdAt = params['created_at'];
  const doneAt = params['done_at'];
  const page = params[pageKey] || defaultPage;
  const pageSize = params[pageSizeKey] || defaultPageSize;
  const sort = params[sortKey];
  const sortField = getSortField(sort);
  const sortDirection = getSortValue(sort) as SortType;

  const overlayId = params[overlayKey];
  const elementId = overlayId?.split('-').pop();
  useFlashElement({
    isReady: !isLoadingFetch && !!list?.length,
    selector: `[data-table-row="${elementId}"]`,
  });

  useEffect(() => {
    fetchEmployees({
      page: 1,
      page_size: 1000,
      is_by_company: true,
      is_available: true,
    });
    fetchBranches({ since_id: 0, n: 1000 });
  }, [fetchBranches, fetchEmployees]);

  const requestParams = useMemo(() => {
    const parsedCreatedAt = formatDateTimeRangeFromString(createdAt);
    const parsedDoneAt = formatDateTimeRangeFromString(doneAt);

    return {
      qr_code: qrCode,
      created_at_from: parsedCreatedAt?.from,
      created_at_to: parsedCreatedAt?.to,
      done_at_from: parsedDoneAt?.from,
      done_at_to: parsedDoneAt?.to,
      name,
      employee,
      status,
      comment,
      client_comment: clientComment,
      field: sortField,
      dir: sortDirection,
      page,
      page_size: pageSize,
      is_by_company: true,
      place_ids: branchId,
      type,
    };
  }, [
    branchId,
    page,
    pageSize,
    clientComment,
    comment,
    createdAt,
    doneAt,
    employee,
    fetchTaskList,
    name,
    qrCode,
    sortDirection,
    sortField,
    status,
    type,
    viewStyle,
  ]);

  useEffect(() => {
    fetchTaskList(requestParams);
  }, [requestParams]);

  const handleEnd = () => {
    if (isLoadingFetch || !pagination || pagination.is_last) return;
    const nextPage = pagination.page + 1;
    fetchTaskList({ ...requestParams, page: nextPage, infinite: true });
  };

  const taskInfoModal = useModalParams('taskInfo');
  const taskFormModal = useModalParams('taskForm');
  const taskCallbackModal = useModalParams('taskCallback');
  const createTaskModal = useModalParams('createTask');

  
  return {
    t,
    pagination,
    viewStyle,
    handleEnd,
    pageTitle: taskTypeMap[type],
    taskInfoModal,
    taskFormModal,
    taskCallbackModal,
    createTaskModal,
  };
};
